/**
 * WorkOrderRamsPanel — embedded under the WO detail dialog. Shows the
 * current RAMS version (or "generating…" while GPT-5.2 runs in the
 * background after WO creation), with admin actions:
 *   · Open PDF
 *   · Regenerate (creates a new version, never overwrites)
 *   · Sign-off (locks the document for audit)
 *
 * Read-allowed for engineers (no edit/regenerate).
 */
import React, { useEffect, useRef, useState } from 'react';
import { ShieldAlert, RefreshCw, Lock, Unlock, History, Eye, Download, CheckCircle2, AlertTriangle, Loader2, PenTool, Eraser, Mail, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import api, { fetchRamsPdfUrl, triggerDownload, acknowledgeRams, getRamsProgress, getRamsSigners, previewRamsEmail, emailRams } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PdfPreviewDialog } from './PdfPreviewDialog';
import SignaturePad from './SignaturePad';

const RISK_TONE = {
  Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-800 border-amber-200',
  High: 'bg-orange-50 text-orange-800 border-orange-200',
  'Very High': 'bg-red-50 text-red-700 border-red-200',
};

// Format a UTC ISO timestamp as a local-time "YYYY-MM-DD HH:mm:ss"
// string. The previous implementation just sliced the raw ISO which
// displayed UTC as if it were local — so a RAMS generated at 13:13
// BST showed as "12:13" in the PDF metadata. JS `new Date()` parses
// ISO 8601 correctly; we format via the en-GB locale (24h) and
// rearrange into ISO-style for tabular display.
const fmtTs = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return (iso || '').slice(0, 19).replace('T', ' ');
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
       + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const WorkOrderRamsPanel = ({ workOrderId, latestRamsAt, wo, onAckChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEngineer = user?.role === 'employee' || user?.role === 'subcontractor';
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [acking, setAcking] = useState(false);
  // Optimistic ack. The moment the ack POST succeeds we flip this to
  // the version's id so the UI reflects "read" immediately — the
  // parent's WO refetch still happens for durability, but the button
  // stops being clickable the instant the network round-trip returns.
  // Without this the engineer sees a stale button (and a stale amber
  // banner) until the parent's refetch propagates back down.
  const [ackedLocalVersionId, setAckedLocalVersionId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  // Signature pads. Distinct refs so the "Mark as read" and "Sign off"
  // dialogs never share stroke state (a mid-drawing dialog swap used to
  // duplicate the pad).
  const ackPadRef = useRef(null);
  const signPadRef = useRef(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [signName, setSignName] = useState(user?.name || '');
  // Required-signers status + share-by-email dialog state. Fetched
  // every time the RAMS versions refresh so the "Share via email"
  // button correctly enables the moment the last engineer acks.
  const [signersStatus, setSignersStatus] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({
    to: '', subject: '', note: '', cc_admin: true,
  });
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  // Backdate / forward-date the sign-off (admin can record a paper RAMS
  // that was physically signed last Tuesday). Defaults to today; backend
  // accepts any ISO date and stamps noon UTC for plain dates so the day
  // never tz-shifts.
  const [signDate, setSignDate] = useState(() => new Date().toISOString().slice(0, 10));
  // In-page PDF preview: holds the blob URL of the currently-previewed RAMS
  // (null = closed). We revoke it on close so memory stays tidy.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);

  const refresh = () => api.get(`/work-orders/${workOrderId}/rams`)
    .then((r) => setVersions(r.data || []))
    .catch(() => {})
    .finally(() => setLoading(false));

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [workOrderId, latestRamsAt]);

  // Belt-and-braces cleanup: if the component unmounts while a regen
  // is in flight (admin closes the WO dialog), don't keep polling.
  useEffect(() => () => {
    if (regenPollRef.current) {
      clearInterval(regenPollRef.current);
      regenPollRef.current = null;
    }
  }, []);

  const current = versions[0]; // newest first
  const body = current?.body || {};
  const isSigned = !!current?.signOff?.signed;
  // Has this engineer (or the admin acting on their behalf) acknowledged
  // the CURRENT version? Acks are invalidated the moment a new version
  // appears, so regenerating a RAMS forces a re-read before the engineer
  // can close the job.
  const ackedForCurrent = !!current
    && (wo?.ramsAcknowledgedVersionId === current.id
        || ackedLocalVersionId === current.id);
  const ackedStale = !!wo?.ramsAcknowledgedVersionId
    && current && wo.ramsAcknowledgedVersionId !== current.id;

  const acknowledge = async () => {
    if (!current) return;
    // A signature is REQUIRED — the button opens the pad dialog and
    // the actual POST fires from `submitAck` below once the engineer
    // has drawn something.
    setAckOpen(true);
  };

  // Refresh the required-signers status. Runs alongside `refresh()`
  // so the "Share via email" button flips to enabled the moment the
  // last engineer's ack lands.
  const refreshSigners = async (ramsId) => {
    if (!ramsId) { setSignersStatus(null); return; }
    try {
      const s = await getRamsSigners(workOrderId, ramsId);
      setSignersStatus(s);
    } catch (e) {
      // Non-fatal — the panel still works, just no share button.
      setSignersStatus(null);
    }
  };

  useEffect(() => {
    if (current?.id) refreshSigners(current.id);
    else setSignersStatus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, wo?.ramsAcknowledgedVersionId, wo?.signOff?.signedAt]);

  const openEmail = async () => {
    if (!current) return;
    setEmailOpen(true);
    setEmailPreview(null);
    // Prefill from the preview endpoint so the admin sees the same
    // subject/body layout the customer will get.
    try {
      const prev = await previewRamsEmail(workOrderId, current.id, {});
      setEmailPreview(prev);
      setEmailForm(f => ({
        ...f,
        to: prev.customerEmail || '',
        subject: prev.defaultSubject || '',
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build email preview');
    }
  };

  const sendEmail = async ({ test = false } = {}) => {
    if (!current) return;
    setEmailSending(true);
    try {
      const res = await emailRams(workOrderId, current.id, {
        to: emailForm.to.trim() || undefined,
        subject: emailForm.subject.trim() || undefined,
        note: emailForm.note.trim() || undefined,
        cc_admin: emailForm.cc_admin,
        test_to_admin: test,
      });
      toast.success(test ? `Test sent to ${res.to}` : `RAMS emailed to ${res.to}`);
      if (!test) setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    } finally {
      setEmailSending(false);
    }
  };

  const submitAck = async () => {
    if (!current) return;
    const pad = ackPadRef.current;
    if (!pad || pad.isEmpty()) {
      toast.error('Please sign above the dashed line before saving.');
      return;
    }
    setAcking(true);
    try {
      const signature = pad.getDataUrl();
      await acknowledgeRams(workOrderId, current.id, signature);
      setAckedLocalVersionId(current.id);
      setAckOpen(false);
      toast.success('RAMS marked as read — your signature is on the pack.');
      onAckChange?.();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark as read');
    } finally {
      setAcking(false);
    }
  };

  // Live regen progress — populated by `getRamsProgress` polled
  // every 1.5 s while a regenerate POST is in flight. Lets the admin
  // see what stage the LLM is at instead of staring at a frozen
  // 30-60 s spinner.
  const [regenProgress, setRegenProgress] = useState(null);
  const regenPollRef = useRef(null);

  const regenerate = async () => {
    setBusy(true);
    setRegenProgress({ stage: 'starting', sectionsCompleted: 0, sectionsTotal: 0 });
    // Poll the progress endpoint in parallel with the (slow) POST
    // call. We tear the poller down in the finally block whether
    // the POST resolves or rejects.
    regenPollRef.current = setInterval(async () => {
      try {
        const snap = await getRamsProgress(workOrderId);
        if (snap && (snap.stage || snap.status)) setRegenProgress(snap);
      } catch { /* poll is best-effort */ }
    }, 1500);
    try {
      await api.post(`/work-orders/${workOrderId}/rams/generate`);
      toast.success('New RAMS version generated');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate RAMS');
    } finally {
      if (regenPollRef.current) {
        clearInterval(regenPollRef.current);
        regenPollRef.current = null;
      }
      setRegenProgress(null);
      setBusy(false);
    }
  };
  const signOff = async () => {
    if (!signName.trim()) { toast.error('Type your name to sign off'); return; }
    const pad = signPadRef.current;
    if (!pad || pad.isEmpty()) {
      toast.error('Draw your signature in the pad before signing off.');
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/work-orders/${workOrderId}/rams/${current.id}/sign-off`,
        {
          signedBy: signName.trim(),
          signedRole: 'admin',
          // Send the picked date so the sign-off can be backdated.
          // Backend treats the YYYY-MM-DD as noon UTC.
          signedAt: signDate,
          signature: pad.getDataUrl(),
        },
      );
      toast.success('RAMS signed off');
      setSignOpen(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sign-off failed');
    } finally {
      setBusy(false);
    }
  };

  const unsign = async () => {
    if (!current) return;
    if (!window.confirm('Unsign this RAMS? It will return to draft so you can edit, regenerate, or re-sign it. The previous sign-off stays in the audit trail.')) {
      return;
    }
    setBusy(true);
    try {
      await api.post(`/work-orders/${workOrderId}/rams/${current.id}/unsign`);
      toast.success('RAMS unsigned — you can edit or re-sign now');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unsign failed');
    } finally {
      setBusy(false);
    }
  };
  const openPreview = async (ramsId) => {
    setPreviewBusy(true);
    try {
      const { url, filename } = await fetchRamsPdfUrl(workOrderId, ramsId);
      setPreviewUrl(url);
      setPreviewFilename(filename);
    } catch (e) {
      toast.error(e?.message || 'Could not load PDF');
    } finally {
      setPreviewBusy(false);
    }
  };
  const downloadPdf = async (ramsId) => {
    try {
      const { url, filename } = await fetchRamsPdfUrl(workOrderId, ramsId);
      triggerDownload(url, filename);
      // Free the blob shortly after the download has been kicked off
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (e) {
      toast.error(e?.message || 'Could not download PDF');
    }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewFilename('');
  };

  if (loading) {
    return (
      <div className="pt-4 border-t border-slate-200 text-sm text-slate-500" data-testid="rams-loading">
        Loading RAMS…
      </div>
    );
  }

  if (!current) {
    return (
      <div className="pt-4 border-t border-slate-200 space-y-3" data-testid="rams-empty">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          AI Safety pack (RAMS)
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
          No RAMS yet. The system tries to auto-generate one when a work order is created — if the WO has no site
          attached, generation is skipped. Make sure this WO has a site on it, then click <strong>Generate RAMS</strong>.
        </div>
        {isAdmin && (
          <Button onClick={regenerate} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="rams-generate-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} />
            {busy ? 'Generating…' : 'Generate RAMS'}
          </Button>
        )}
        {busy && regenProgress && <RegenProgress progress={regenProgress} />}
      </div>
    );
  }

  return (
    <div className="pt-4 border-t border-slate-200 space-y-3" data-testid="rams-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <ShieldAlert className="h-4 w-4 text-blue-700" />
          AI Safety pack (RAMS)
          <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
            v{current.version || 1}
          </span>
          {isSigned && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
              <Lock className="h-3 w-3" /> Signed
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => openPreview(current.id)} disabled={previewBusy} data-testid="rams-preview-pdf">
            <Eye className="h-3.5 w-3.5 mr-1" /> {previewBusy ? 'Loading…' : 'Preview PDF'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => downloadPdf(current.id)} data-testid="rams-download-pdf">
            <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
          </Button>
          {versions.length > 1 && (
            <Button size="sm" variant="outline" onClick={() => setHistoryOpen(true)} data-testid="rams-history-btn">
              <History className="h-3.5 w-3.5 mr-1" /> History ({versions.length})
            </Button>
          )}
          {isAdmin && !isSigned && (
            <>
              <Button size="sm" variant="outline" onClick={regenerate} disabled={busy} data-testid="rams-regenerate-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} />
                Regenerate
              </Button>
              <Button size="sm" onClick={() => setSignOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="rams-sign-btn">
                <Lock className="h-3.5 w-3.5 mr-1" /> Sign off
              </Button>
            </>
          )}
          {/* Share via email — only enabled once every required signer
              (WO creator + every assigned engineer) has a stored
              signature. Refuses on the server side too, so a rogue
              tap here can't leak an unsigned pack. */}
          {isAdmin && signersStatus && (
            <Button
              size="sm"
              onClick={openEmail}
              disabled={!signersStatus.fullySigned || emailSending}
              className={
                signersStatus.fullySigned
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-slate-100 text-slate-500 cursor-not-allowed'
              }
              title={
                signersStatus.fullySigned
                  ? 'Send the fully-signed RAMS to the customer'
                  : `Waiting on: ${signersStatus.signers.filter(s => !s.hasSignature).map(s => s.name).join(', ')}`
              }
              data-testid="rams-share-email-btn"
            >
              <Mail className="h-3.5 w-3.5 mr-1" />
              {signersStatus.fullySigned
                ? 'Share via email'
                : `Waiting (${signersStatus.signers.filter(s => s.hasSignature).length}/${signersStatus.signers.length})`}
            </Button>
          )}
          {isAdmin && isSigned && (    <Button
              size="sm"
              variant="outline"
              onClick={unsign}
              disabled={busy}
              className="border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid="rams-unsign-btn"
            >
              <Unlock className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} />
              Unsign
            </Button>
          )}
        </div>
      </div>

      <div className="text-[11px] text-slate-500">
        Generated {fmtTs(current.generatedAt)} by {current.model || 'AI'}.
        {isSigned && <> Signed by <strong>{current.signOff.signedBy}</strong> on {fmtTs(current.signOff.signedAt)}.</>}
      </div>

      {/* Site-changed staleness banner. Fires when admin changed the
          WO's physical-site customer AFTER a RAMS was generated — the
          pack now names the WRONG on-site company. Backend stamps
          `wo.ramsStale` + a reason on the site-change PATCH and
          clears it on the next successful regenerate. */}
      {wo?.ramsStale && (
        <div
          className="flex items-center justify-between gap-2 text-xs bg-amber-50 border border-amber-300 rounded-md px-2.5 py-1.5"
          data-testid="rams-stale-banner"
        >
          <div className="flex items-center gap-2 text-amber-900 min-w-0">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {wo.ramsStaleReason === 'site-changed'
                ? 'The site on this WO has changed — the RAMS may name the wrong on-site company.'
                : 'This RAMS may be out of date.'}
            </span>
          </div>
          {isAdmin && !isSigned && (
            <Button
              size="sm"
              onClick={regenerate}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2.5 text-xs shrink-0"
              data-testid="rams-stale-regenerate-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} />
              {busy ? 'Generating…' : 'Regenerate'}
            </Button>
          )}
        </div>
      )}

      {busy && regenProgress && <RegenProgress progress={regenProgress} />}

      {/* Acknowledgement row. Engineers MUST mark the current RAMS as read
          before they can close the job — enforced server-side. Admins see
          the status as a read-receipt for audit. */}
      {ackedForCurrent ? (
        <div
          className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5"
          data-testid="rams-ack-done"
        >
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          <span>
            Read by <strong>{wo.ramsAcknowledgedByName || user?.name || 'engineer'}</strong>
            {wo.ramsAcknowledgedAt && <> on {fmtTs(wo.ramsAcknowledgedAt)}</>}
          </span>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 text-xs bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
          <div className="flex items-center gap-2 text-amber-900 min-w-0">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {ackedStale
                ? `A newer RAMS version (V${current?.version || '?'}) was generated — please re-read before closing this job.`
                : 'You must read this RAMS before you can close the job.'}
            </span>
          </div>
          {isEngineer && (
            <Button
              size="sm"
              onClick={acknowledge}
              disabled={acking}
              className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2.5 text-xs shrink-0"
              data-testid="rams-mark-read-btn"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              {acking ? 'Saving…' : 'Mark as read'}
            </Button>
          )}
        </div>
      )}

      {body.scope && (
        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-2.5 leading-relaxed">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Scope</div>
          {body.scope}
        </div>
      )}

      {/* AI-incomplete warning — surfaces sections the LLM couldn't
          fill even after per-section top-up calls. Without this the
          admin might sign off a doc with no risk table / PPE etc.
          and not notice until it's downloaded. */}
      {Array.isArray(body.incompleteSections) && body.incompleteSections.length > 0 && (
        <div
          className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-2.5 leading-relaxed flex items-start gap-2"
          data-testid="rams-incomplete-warning"
        >
          <span aria-hidden className="text-amber-700">⚠</span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold mb-0.5">AI couldn't fill {body.incompleteSections.length} section{body.incompleteSections.length === 1 ? '' : 's'}</div>
            <div className="text-[11px]">
              Missing: <span className="font-mono">{body.incompleteSections.map((s) => s.replace(/_/g, ' ')).join(', ')}</span>.
              Click <strong>Regenerate</strong> to try again or edit the RAMS to add this content manually.
            </div>
          </div>
        </div>
      )}

      {/* Risk-assessment summary (top 5) */}
      {Array.isArray(body.risk_assessment) && body.risk_assessment.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Risk summary</div>
          <div className="space-y-1.5">
            {body.risk_assessment.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className={`inline-flex shrink-0 items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${RISK_TONE[r.rating] || RISK_TONE.Low}`}>
                  {r.rating}
                </span>
                <span className="text-slate-700 min-w-0 flex-1 break-words">{r.hazard}</span>
              </div>
            ))}
            {body.risk_assessment.length > 5 && (
              <div className="text-[10px] text-slate-500">+ {body.risk_assessment.length - 5} more — see PDF for full pack</div>
            )}
          </div>
        </div>
      )}

      {Array.isArray(body.ppe) && body.ppe.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-12 shrink-0 self-center">PPE</span>
          {body.ppe.slice(0, 8).map((p, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-300 text-slate-700">{p}</span>
          ))}
        </div>
      )}

      {/* Sign-off dialog */}
      <Dialog
        open={signOpen}
        onOpenChange={(v) => {
          setSignOpen(v);
          // Reset the date back to today every time the dialog opens
          // so the user doesn't accidentally re-use yesterday's pick.
          if (v) setSignDate(new Date().toISOString().slice(0, 10));
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="rams-sign-dialog">
          <DialogHeader>
            <DialogTitle>Sign off RAMS v{current.version || 1}</DialogTitle>
            <DialogDescription>
              Once signed, this version becomes <strong>read-only</strong> and lands in the audit trail.
              Need to make changes? Cancel and click <strong>Regenerate</strong> first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Approver name</Label>
              <Input value={signName} onChange={(e) => setSignName(e.target.value)} className="mt-1.5" data-testid="rams-sign-name" />
            </div>
            <div>
              <Label>Sign-off date</Label>
              <Input
                type="date"
                value={signDate}
                onChange={(e) => setSignDate(e.target.value)}
                className="mt-1.5"
                data-testid="rams-sign-date"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Defaults to today. Backdate (or forward-date) when logging a paper RAMS that was signed on a different day.
              </p>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <PenTool className="h-3.5 w-3.5" /> Signature
              </Label>
              <div className="mt-1.5">
                <SignaturePad ref={signPadRef} height={160} disabled={busy} />
              </div>
              <button
                type="button"
                onClick={() => signPadRef.current?.clear()}
                className="mt-1 text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                data-testid="rams-sign-clear"
              >
                <Eraser className="h-3 w-3" /> Clear signature
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)}>Cancel</Button>
            <Button onClick={signOff} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="rams-sign-confirm">
              <Lock className="h-4 w-4 mr-1" /> {busy ? 'Signing…' : 'Sign off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="rams-history-dialog">
          <DialogHeader>
            <DialogTitle>RAMS version history</DialogTitle>
            <DialogDescription>Each regeneration creates a new version — older versions stay for audit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 p-2 border border-slate-200 rounded-md text-sm">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">v{v.version || 1} · {v.title}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {fmtTs(v.generatedAt)}
                    {v.signOff?.signed && <> · signed by {v.signOff.signedBy}</>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => openPreview(v.id)}
                    disabled={previewBusy}
                    data-testid={`rams-history-preview-${v.id}`}
                    title="Preview PDF"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => downloadPdf(v.id)}
                    data-testid={`rams-history-download-${v.id}`}
                    title="Download PDF"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* In-page PDF preview */}
      <PdfPreviewDialog
        url={previewUrl}
        filename={previewFilename}
        onClose={closePreview}
        title="RAMS PDF preview"
      />

      {/* Read-receipt signature dialog. Engineer must draw a signature
          before the ack POST fires — signature ends up on the RAMS PDF
          "Read receipts" table. */}
      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent className="sm:max-w-md" data-testid="rams-ack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" />
              Confirm you've read RAMS v{current?.version || 1}
            </DialogTitle>
            <DialogDescription>
              Your name, the time, and this signature will appear on every PDF export of this RAMS
              as an audit record of the read receipt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <SignaturePad ref={ackPadRef} height={180} disabled={acking} />
            <button
              type="button"
              onClick={() => ackPadRef.current?.clear()}
              className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
              data-testid="rams-ack-clear"
            >
              <Eraser className="h-3 w-3" /> Clear signature
            </button>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAckOpen(false)} disabled={acking}>Cancel</Button>
            <Button
              onClick={submitAck}
              disabled={acking}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="rams-ack-confirm"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              {acking ? 'Saving…' : 'Confirm read receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share via email dialog — mirrors the invoice / quote email
          preview: recipient, editable subject, admin note, CC-admin
          toggle, "Send test to me" and "Send" buttons. Preview HTML
          is rendered in an iframe so the admin sees the same layout
          the customer will receive. */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="rams-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-700" />
              Share signed RAMS
            </DialogTitle>
            <DialogDescription>
              Sends the fully-signed PDF to the customer in the same branded
              layout as the invoice and quote emails.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label>Recipient</Label>
                <Input
                  value={emailForm.to}
                  onChange={(e) => setEmailForm(f => ({ ...f, to: e.target.value }))}
                  placeholder="customer@example.com"
                  data-testid="rams-email-to"
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(f => ({ ...f, subject: e.target.value }))}
                  data-testid="rams-email-subject"
                />
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  rows={4}
                  value={emailForm.note}
                  onChange={(e) => setEmailForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="Add a short line just for this customer…"
                  data-testid="rams-email-note"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={emailForm.cc_admin}
                  onChange={(e) => setEmailForm(f => ({ ...f, cc_admin: e.target.checked }))}
                  data-testid="rams-email-cc-admin"
                />
                CC the admin inbox
              </label>
              {signersStatus && (
                <div className="text-[11px] text-slate-500 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5 flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-emerald-700" />
                  Signed by {signersStatus.signers.length} {signersStatus.signers.length === 1 ? 'person' : 'people'}: {signersStatus.signers.map(s => s.name).join(', ')}
                </div>
              )}
            </div>
            <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
              {emailPreview ? (
                <iframe
                  title="RAMS email preview"
                  srcDoc={emailPreview.html}
                  className="w-full h-[380px]"
                  data-testid="rams-email-preview-iframe"
                />
              ) : (
                <div className="h-[380px] flex items-center justify-center text-xs text-slate-400">
                  Loading preview…
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => sendEmail({ test: true })}
              disabled={emailSending}
              data-testid="rams-email-test-btn"
            >
              Send test to me
            </Button>
            <Button
              onClick={() => sendEmail({ test: false })}
              disabled={emailSending || !emailForm.to.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="rams-email-send-btn"
            >
              <Mail className="h-4 w-4 mr-1" />
              {emailSending ? 'Sending…' : 'Send to customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderRamsPanel;

/**
 * Inline live-progress banner for the RAMS regenerate flow. Polled
 * every 1.5 s while POST `/work-orders/.../rams/generate` is in
 * flight so the admin always sees what the LLM is doing. Stages:
 *   gathering   — pulling WO + site + asset + library docs from Mongo
 *   drafting    — first GPT-5.2 call producing the full pack
 *   retrying    — first response was incomplete, regenerating
 *   topping_up  — per-section parallel calls filling the gaps
 *   done        — saved
 *   error       — POST returned 5xx; toast shows the detail
 */
// eslint-disable-next-line react/prop-types
const RegenProgress = ({ progress }) => {
  if (!progress) return null;
  const stage = progress.stage || 'starting';
  const completed = progress.sectionsCompleted || 0;
  const total = progress.sectionsTotal || 0;
  const label = (() => {
    if (stage === 'starting' || stage === 'pending') return 'Starting…';
    if (stage === 'gathering') return 'Pulling work order, site, asset & library docs…';
    if (stage === 'drafting') return 'Drafting the full RAMS pack with GPT-5.2…';
    if (stage === 'retrying') return 'First draft was incomplete — retrying with a corrective prompt…';
    if (stage === 'topping_up') {
      return total > 0
        ? `Filling ${completed} of ${total} missing section${total === 1 ? '' : 's'} in parallel…`
        : 'Filling in missing sections…';
    }
    if (stage === 'done') return 'Finalising…';
    if (stage === 'error') return 'Generation failed — see the error toast for details.';
    return stage;
  })();
  return (
    <div
      className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 flex items-center gap-3 text-xs text-blue-900"
      data-testid="rams-regen-progress"
      role="status"
      aria-live="polite"
    >
      <Loader2 className={`h-3.5 w-3.5 shrink-0 ${stage === 'error' ? 'text-red-700' : 'text-blue-700 animate-spin'}`} />
      <div className="flex-1 min-w-0">
        <div className="font-medium">{label}</div>
        {stage === 'topping_up' && total > 0 && (
          <div className="mt-1 h-1 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-[width] duration-500 ease-out"
              style={{ width: `${Math.min(100, (completed / total) * 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
